// Внутренние ссылки для поиска (29.10.2026): Search Console — 280 страниц
// «обнаружена, не проиндексирована», Google находит их почти только через
// sitemap. Здесь — связи между разделами, без серверных импортов:
//   предмет репетитора ↔ профессии карты профессий (только очевидные связи),
//   статья блога → профессии, адреса профессий и бесплатного теста.
// Страница предмета есть не всегда (≥ 3 анкет, src/lib/tutor-subject-pages.ts) —
// какие есть, приходит с сервера (getSubjectPages), ссылку на 404 не ставим.
// Проверка — scripts/verify-seo-internal-links.mts.
import { careerNameOf, sphereSlugOf } from "@/careers/data";
import type { Lang3 } from "@/data/map-taxonomy";

/**
 * Предмет → профессии (id из src/data/career_pages). Только там, где связь
 * видна родителю без объяснений; у служебных предметов (домашка, начальные
 * классы, экзамены, музыка) связей нет — блока нет.
 */
export const SUBJECT_CAREERS: Record<string, string[]> = {
  math: ["data_analyst", "financial_analyst", "civil_engineer", "software_developer", "accountant"],
  geometry: ["architect", "civil_engineer"],
  physics: ["energy_engineer", "electrician", "pilot", "petroleum_engineer"],
  chemistry: ["pharmacist", "food_technologist", "petroleum_engineer", "ecologist"],
  biology: ["doctor", "dentist", "veterinarian", "pharmacist", "ecologist"],
  geography: ["geologist", "ecologist", "diplomat"],
  history: ["diplomat", "lawyer", "judge", "journalist"],
  az_lang: ["journalist", "translator", "lawyer"],
  literature: ["journalist", "translator", "teacher"],
  ru_lang: ["translator", "journalist"],
  informatics: ["software_developer", "data_analyst", "network_engineer", "cybersecurity", "ai_engineer"],
  logic: ["software_developer", "data_analyst", "ai_engineer"],
  drawing_tech: ["architect", "civil_engineer"],
  en: ["translator", "diplomat", "hotel_manager"],
  tr: ["translator", "diplomat"],
  ru: ["translator", "diplomat"],
  de: ["translator", "diplomat"],
  fr: ["translator", "diplomat"],
  es: ["translator", "diplomat"],
  it: ["translator", "diplomat"],
  ar: ["translator", "diplomat"],
  zh: ["translator", "diplomat"],
  ja: ["translator", "diplomat"],
  coding: ["software_developer", "game_developer", "ai_engineer", "data_scientist"],
  office: ["accountant"],
  graphic: ["graphic_designer", "ux_designer", "content_creator"],
  uiux: ["ux_designer", "product_manager", "graphic_designer"],
  "3d": ["game_developer", "architect"],
  drawing: ["architect", "graphic_designer", "fashion_designer"],
};

export const careersForSubject = (code: string): string[] => SUBJECT_CAREERS[code] ?? [];

/** Профессия → предметы, которые для неё нужны (обратная сторона SUBJECT_CAREERS). */
export const subjectsForCareer = (id: string): string[] => Object.keys(SUBJECT_CAREERS).filter((s) => SUBJECT_CAREERS[s].includes(id));

const CAREERS_ROOT: Record<Lang3, string> = { az: "/az/careers", ru: "/careers", en: "/en/careers" };

/** Адрес страницы профессии; нет сферы — null (ссылку не ставим). */
export function careerHref(id: string, lang: Lang3): string | null {
  const sphere = sphereSlugOf(id);
  return sphere ? `${CAREERS_ROOT[lang]}/${sphere}/${id}` : null;
}

export const sphereHref = (slug: string, lang: Lang3) => `${CAREERS_ROOT[lang]}/${slug}`;

export type CareerLink = { id: string; name: string; href: string };

/** Ссылки на профессии: только те, у которых есть имя и сфера. */
export function careerLinks(ids: string[], lang: Lang3): CareerLink[] {
  return ids.flatMap((id) => {
    const href = careerHref(id, lang);
    const name = careerNameOf(id, lang);
    return href && name ? [{ id, name, href }] : [];
  });
}

/** Бесплатный тест — тот же адрес, что в подвале. */
export { FREE_TEST_PATH as FREE_TEST_HREF } from "@/components/free-test/free-test-copy";

/**
 * Статья блога → 2 профессии. Ключ — translation_group статьи (одна на три
 * языка). Новая статья без строки — профессии по умолчанию.
 */
export const BLOG_CAREERS: Record<string, string[]> = {
  // «Как определить способности ребёнка»
  "1175f876": ["educational_psychologist", "psychologist"],
  // «Учёба за рубежом в 2027»
  "3b0f6a52": ["diplomat", "translator"],
};
export const BLOG_CAREERS_DEFAULT = ["educational_psychologist", "teacher"];
export const blogCareers = (translationGroup: string): string[] =>
  BLOG_CAREERS[translationGroup.slice(0, 8)] ?? BLOG_CAREERS_DEFAULT;
